import type {
  CadenceStepStatePure,
  CadenceStepTypePure,
  SiftConditionPure,
  SiftConfigPure,
} from '../CGStep/types';
import {
  SIFT_FIELD_OPTIONS_PURE,
  SIFT_OPERATOR_LABELS_PURE,
  SIFT_OPERATORS_WITHOUT_VALUE_PURE,
} from '../CGStep/types';
import { stripHtmlTags } from '@poseidon/shared-utils/html';

export type CampaignDayCardStep = {
  id: string;
  type: CadenceStepTypePure;
  /**
   * Runtime state of the step. Drives state-based row tinting — e.g.
   * a `reminder` step in `'action-required'` state renders green to
   * tell the user "this is waiting on you".
   */
  state?: CadenceStepStatePure;
  body2?: string | null;
  subject?: string | null;
  postsToLike?: number | null;
  cc?: string | null;
  bcc?: string | null;
  /** Sift condition payload — used by sift step display + editor. */
  siftConditions?: SiftConfigPure | null;
  /**
   * InMail-only flag. When true, the step is sent ONLY when no InMail
   * credit is needed (Open Profile recipients). Forwarded into the
   * inline editor's toggle.
   */
  onlyOpenLink?: boolean | null;
  /**
   * Connect-only flag. When true (default), a still-pending LinkedIn invitation is auto-withdrawn
   * once this connect step's not-accepted ("No") branch resolves. Forwarded into the inline
   * editor's toggle.
   */
  autoWithdraw?: boolean | null;
  /** True when this step's content has been edited away from its template default. */
  isDifferentFromTemplate?: boolean;
  /**
   * Error message to surface on the row (e.g. "Variable {first_name} is not
   * defined", "Contact has no email address"). When present the row tints
   * red and the message is shown under the body.
   */
  error?: string | null;
  /**
   * Reason code for a `skipped` step (e.g. `follow_rejected`, `already_followed`).
   * Mapped to a human label via {@link getSkipLabel} and shown as a muted
   * sub-line — NOT the red error styling. Only meaningful when `state === 'skipped'`.
   */
  skipReason?: string | null;
};

export type StepDisplayExtra = { label: string; value: string };

/**
 * A run of plain text or a single resolved variable span. Surfaced on
 * `StepDisplay.primarySegments` / `bodySegments` so renderers can colour
 * variable resolutions distinctly from the surrounding prose. This is
 * how we communicate "the word `Michael` here is the resolved value of
 * `@First Name` — it'll be re-resolved per contact at send time" without
 * dropping back to showing raw template tokens.
 */
export type StepDisplaySegment =
  | { kind: 'text'; text: string }
  | { kind: 'variable'; text: string; id: string };

export type SiftConditionParts = {
  /** Human label for the field (e.g. "Job title"). */
  field: string;
  /** Human label for the operator (e.g. "contains", "is empty"). */
  operator: string;
  /**
   * Display value, or `undefined` when the operator doesn't take one
   * (e.g. is_empty / is_not_empty). Empty string means "operator needs
   * a value but none was given yet" — render a placeholder like "…".
   */
  value?: string;
};

export type StepDisplay = {
  /** Primary text — the meaningful content of the step. Always present. */
  primary: string;
  /**
   * Structured form of `primary` — present whenever the primary string
   * was sourced from rendered HTML that contained variable mentions
   * (sent-text steps: message / connect / inmail subject / email
   * subject / reminder). Renderers should prefer this when present so
   * variable resolutions can be coloured distinctly. When absent fall
   * back to the plain `primary` string.
   */
  primarySegments?: StepDisplaySegment[];
  /**
   * True when the `primary` line holds wrappable BODY content (a message
   * body, a connection-request note, or a subject-less inmail/email body)
   * rather than a one-line subject or a short action label. Row renderers
   * use it to decide whether the primary wraps (`whitespace-pre-wrap`) or
   * clips to one line (`truncate`). Subjects and action labels leave it
   * falsy. The separate `body` field below always wraps regardless.
   */
  wrapPrimary?: boolean;
  /**
   * Full message body for message / email / inmail. Rendered as a wrapped,
   * untruncated block under the primary line. Omitted when there's no
   * separate body to show (e.g. visit, follow, like).
   */
  body?: string;
  /** Structured form of `body`, same contract as `primarySegments`. */
  bodySegments?: StepDisplaySegment[];
  /** Optional additional fields to render under the primary (e.g. CC / BCC for email). */
  extras?: StepDisplayExtra[];
  /**
   * Structured sift conditions — when present the row renders each condition
   * as a chip-style line where field / operator / value are visually distinct.
   * Used for sift steps; takes precedence over `body` for those.
   */
  siftRows?: SiftConditionParts[];
};

const MAX_DETAIL = 80;

function truncate(text: string): string {
  if (text.length <= MAX_DETAIL) return text;
  return `${text.slice(0, MAX_DETAIL - 1).trimEnd()}…`;
}

const HTML_ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

function decodeEntities(s: string): string {
  return s.replace(/&(?:nbsp|amp|lt|gt|quot|#39);/g, (m) => HTML_ENTITIES[m] ?? m);
}

function stripTagsToPlain(s: string): string {
  // Delegates to the shared helper so the row preview honors block-level
  // line breaks (`<br>`, `</p>`, `</div>`) instead of collapsing every
  // tag to a single space. Same semantics as outbound message bodies
  // (unipile, worker), so what reps see in the row matches what gets
  // sent over the wire.
  return stripHtmlTags(s);
}

/**
 * Parse server-expanded body / subject HTML into a flat list of segments,
 * preserving variable mentions (`<span data-id="…">value</span>`) as
 * `{ kind: 'variable' }` entries so the row can colour them distinctly.
 *
 * Two-pass approach so whitespace normalization stays consistent with
 * the plain-text path in `htmlToText`:
 *   1. Replace mention spans with U+0000-bracketed placeholders.
 *   2. Strip the rest of the HTML and collapse whitespace exactly the
 *      same way `htmlToText` does.
 *   3. Split the resulting plain text on placeholders, re-interleave
 *      with the captured mention spans.
 *
 * Mentions are detected by the `data-id` attribute, which the server's
 * `convertTemplateToHtml` preserves even after replacing the inner text
 * with the resolved value (see
 * `packages/server/src/api/utils/htmlUtils.ts:89`).
 */
function htmlToSegments(html: string | null | undefined): StepDisplaySegment[] {
  if (html == null || html.length === 0) return [];
  const mentions: { text: string; id: string }[] = [];
  const withPlaceholders = html.replace(
    /<\w+([^>]*?)>([^<]*)<\/\w+>/g,
    (whole, attrs: string, inner: string) => {
      const idMatch = /data-id="([^"]*)"/.exec(attrs);
      if (idMatch == null) return whole; // not a mention; let stripTags handle it
      mentions.push({ text: decodeEntities(inner), id: idMatch[1]! });
      return ` M${mentions.length - 1} `;
    },
  );
  const plain = stripTagsToPlain(withPlaceholders).trim();
  if (plain.length === 0) return [];
  const parts = plain.split(/ M(\d+) /);
  const out: StepDisplaySegment[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      const t = parts[i] ?? '';
      if (t.length > 0) out.push({ kind: 'text', text: t });
    } else {
      const m = mentions[Number(parts[i])];
      if (m != null && m.text.length > 0) {
        out.push({ kind: 'variable', text: m.text, id: m.id });
      }
    }
  }
  return out;
}

function segmentsToText(segments: StepDisplaySegment[]): string {
  return segments.map((s) => s.text).join('');
}

export function getStepDisplay(step: CampaignDayCardStep): StepDisplay {
  switch (step.type) {
    case 'visit':
      return { primary: 'Will visit the profile page' };
    case 'follow':
      return { primary: 'Will follow the profile page' };
    case 'likeRecentPosts': {
      const n = step.postsToLike ?? 1;
      return { primary: `Will like up to ${n} ${n === 1 ? 'post' : 'posts'}` };
    }
    case 'connect': {
      const segs = htmlToSegments(step.body2);
      const text = segmentsToText(segs);
      if (text.length === 0) return { primary: 'Will send a connection request' };
      // The connection note is authored content (like a message body) —
      // show it in full so the row can wrap it across lines, rather than
      // cropping to a single line. The row keeps `primarySegments` so the
      // variable colouring still applies.
      return { primary: text, primarySegments: segs, wrapPrimary: true };
    }
    case 'message': {
      const segs = htmlToSegments(step.body2);
      const text = segmentsToText(segs);
      // Message has no subject — the body IS the primary, shown in full.
      if (text.length === 0) return { primary: 'Empty message' };
      return { primary: text, primarySegments: segs, wrapPrimary: true };
    }
    case 'inmail': {
      const subjectSegs = htmlToSegments(step.subject);
      const bodySegs = htmlToSegments(step.body2);
      const subject = segmentsToText(subjectSegs);
      const body = segmentsToText(bodySegs);
      if (subject.length === 0 && body.length === 0) {
        return { primary: 'Empty InMail' };
      }
      const useSubjectAsPrimary = subject.length > 0;
      return {
        primary: useSubjectAsPrimary ? subject : body,
        primarySegments: useSubjectAsPrimary ? subjectSegs : bodySegs,
        // Subject stays a one-line primary; a subject-less inmail promotes
        // the body into the primary, which must wrap. The separate `body`
        // field below always wraps regardless.
        wrapPrimary: !useSubjectAsPrimary,
        body: useSubjectAsPrimary && body.length > 0 ? body : undefined,
        bodySegments: useSubjectAsPrimary && body.length > 0 ? bodySegs : undefined,
      };
    }
    case 'email': {
      const subjectSegs = htmlToSegments(step.subject);
      const bodySegs = htmlToSegments(step.body2);
      const subject = segmentsToText(subjectSegs);
      const body = segmentsToText(bodySegs);
      const cc = (step.cc ?? '').trim();
      const bcc = (step.bcc ?? '').trim();
      const extras: StepDisplayExtra[] = [];
      if (cc.length > 0) extras.push({ label: 'CC', value: cc });
      if (bcc.length > 0) extras.push({ label: 'BCC', value: bcc });
      const hasSubject = subject.length > 0;
      const hasBody = body.length > 0;
      const primaryStr = hasSubject ? subject : hasBody ? body : 'Empty email';
      const primarySegs = hasSubject ? subjectSegs : hasBody ? bodySegs : undefined;
      return {
        primary: primaryStr,
        primarySegments: primarySegs,
        // Subject stays a one-line primary; a subject-less email promotes the
        // body into the primary, which must wrap. The separate `body` field
        // below always wraps regardless.
        wrapPrimary: !hasSubject && hasBody,
        body: hasSubject && hasBody ? body : undefined,
        bodySegments: hasSubject && hasBody ? bodySegs : undefined,
        extras: extras.length > 0 ? extras : undefined,
      };
    }
    case 'reminder': {
      const segs = htmlToSegments(step.body2);
      const text = segmentsToText(segs);
      if (text.length === 0) return { primary: 'Reminder' };
      return { primary: truncate(text), primarySegments: segs };
    }
    case 'sift': {
      const conditions = step.siftConditions?.conditions ?? [];
      if (conditions.length === 0) {
        return { primary: 'Filter contacts (no conditions yet)' };
      }
      return {
        primary: 'Filter contacts where:',
        siftRows: conditions.map(siftConditionParts),
      };
    }
    case 'call': {
      const segs = htmlToSegments(step.body2);
      const text = segmentsToText(segs);
      if (text.length === 0) return { primary: 'Call contact' };
      // Surface the saved call script under the action label, like an email body.
      return { primary: 'Call contact', body: text, bodySegments: segs };
    }
    case 'voice_note':
      return { primary: 'Send voice note' };
    case 'switch_campaign':
      // The destination campaign isn't available to this pure renderer; surfaces that
      // need it (the Touch Campaign Graph card) resolve + show it themselves. Everyone
      // else at least gets a clean label instead of the raw snake_case type.
      return { primary: 'Change to campaign' };
    case 'delay':
    case 'wait_for_connect':
      return { primary: step.type };
    default:
      return { primary: step.type };
  }
}

/**
 * Human-readable labels for `cadence_step.skip_reason` codes. Keep in sync with
 * the reasons written server-side (e.g. `cadenceStepUnipileExec.service.ts` and
 * `pg.cadenceStep.service.ts`). Codes not listed here fall back to a generic
 * "Skipped" via {@link getSkipLabel}.
 */
const SKIP_REASON_LABELS: Record<string, string> = {
  follow_rejected: "Skipped — couldn't follow this profile",
  like_rejected: "Skipped — couldn't like recent posts",
  already_followed: 'Skipped — already following',
  profile_not_open: 'Skipped — profile not open to InMail',
  'already connected': 'Skipped — already connected',
  'already sent connection request': 'Skipped — connection request already sent',
};

/**
 * Returns the muted sub-label for a skipped step, or `null` when the step
 * isn't skipped. Unknown reason codes (and skips with no reason) render a
 * generic "Skipped" so the row never silently looks like a pending action.
 */
export function getSkipLabel(step: CampaignDayCardStep): string | null {
  if (step.state !== 'skipped') return null;
  const reason = step.skipReason ?? '';
  return SKIP_REASON_LABELS[reason] ?? 'Skipped';
}

/**
 * Red status info for a step that is interrupted or carries a
 * template/runtime error. Returns a compact `label` for the row's status
 * chip plus the full `reason` for its hover tooltip, or `null` when the step
 * is neither interrupted nor errored.
 *
 * The row keys its red treatment off THIS (i.e. off the authoritative
 * `state`), not merely off the presence of an error string — so an
 * `interrupted` step whose server `error_msg` came back empty still renders
 * red and explains itself on hover ("This step was interrupted…") instead of
 * masquerading as a normal pending row. `step.error` already folds in
 * `errorMessage` + `computedError` (template/variable errors), so a
 * not-started step with a config error still surfaces as an "Error".
 */
export function getStepErrorInfo(step: CampaignDayCardStep): { label: string; reason: string } | null {
  const isInterrupted = step.state === 'interrupted';
  const message = step.error != null && step.error.length > 0 ? step.error : null;
  if (!isInterrupted && message == null) return null;
  return {
    label: isInterrupted ? 'Interrupted' : 'Error',
    reason: message ?? 'This step was interrupted before it could run.',
  };
}

/**
 * Format a single sift condition as a one-line readable string, e.g.
 *   - "Job title contains CTO"
 *   - "Company is empty"
 *   - "Estimated age is at least 30"
 *
 * Uses the pure label maps from CGStep/types so the output stays
 * platform-agnostic. Falls back to the raw field/operator names for any
 * value the maps don't know about (defensive — handles legacy data).
 */
export function formatSiftCondition(condition: SiftConditionPure): string {
  const parts = siftConditionParts(condition);
  if (parts.value === undefined) return `${parts.field} ${parts.operator}`;
  if (parts.value.length === 0) return `${parts.field} ${parts.operator} …`;
  return `${parts.field} ${parts.operator} ${parts.value}`;
}

/**
 * Structured form of {@link formatSiftCondition} — returns the field /
 * operator / value pieces separately so the UI can style each one
 * differently (bold field, muted operator, chip-style value).
 *
 * `value` is `undefined` for operators that don't take one
 * (`is_empty`, `is_not_empty`). For value-taking operators it is the
 * trimmed string, possibly empty (UI should render a placeholder).
 */
export function siftConditionParts(condition: SiftConditionPure): SiftConditionParts {
  const field =
    SIFT_FIELD_OPTIONS_PURE.find((f) => f.field === condition.field)?.label ?? condition.field;
  const operator = SIFT_OPERATOR_LABELS_PURE[condition.operator] ?? condition.operator;
  if (SIFT_OPERATORS_WITHOUT_VALUE_PURE.has(condition.operator)) {
    return { field, operator };
  }
  return { field, operator, value: (condition.value ?? '').trim() };
}
